import { useState } from 'react';
import { apiKeyManager } from '@empiriecom/mybuy-session/ApiKey';
import type { RequestPolicy } from 'urql';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { CheckoutOrderableBasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/CheckoutOrderableBasketFragmentFragmentDoc';
import { GetPreparedBasketDocument } from '@packages/gql/generated/shopping/GetPreparedBasketDocument';
import type {
  CheckoutOrderableBasketFragmentFragment,
  GetPreparedBasketQueryVariables,
} from '@packages/gql/generated/shopping/graphql';
import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';

/**
 * The unwrapped orderable basket as returned by the GetPreparedBasket query,
 * i.e. the fragment data plus its discriminating `__typename`.
 */
export type OrderableBasket = {
  __typename: 'CheckoutOrderableBasket';
} & CheckoutOrderableBasketFragmentFragment;

/**
 * A single allowed payment as returned by the GetPreparedBasket query. Derived
 * from the fragment so the type always matches the fields actually selected
 * (use this instead of the broad schema type `CheckoutOrderableBasketAllowedPayments`).
 */
export type OrderableAllowedPayment = NonNullable<
  CheckoutOrderableBasketFragmentFragment['allowedPayments'][number]
>;

/**
 * Phone numbers available for the current order, derived from the fragment so
 * the type always matches the fields actually selected (use this instead of the
 * broad schema type `CheckoutPhoneNumbers`).
 */
export type OrderablePhoneNumbers = NonNullable<
  CheckoutOrderableBasketFragmentFragment['phoneNumbers']
>;

export const GetPreparedBasketQuery = /* GraphQL */ `
  query GetPreparedBasket(
    $locale: String!
    $page: CheckoutTargetPage!
    $coupons: String
    $loyaltyCoupons: String
    $addHints: String
    $fp: String
  ) {
    getPreparedBasket(
      locale: $locale
      page: $page
      coupons: $coupons
      loyaltyCoupons: $loyaltyCoupons
      addHints: $addHints
      fp: $fp
    ) {
      __typename
      ... on CheckoutOrderableBasket {
        ...CheckoutOrderableBasketFragment
      }
      ... on CheckoutServerFailure {
        failureMessage
        failureType
      }
    }
  }
`;

// Must map "no codes" to `undefined`, not `''` - urql's `stringifyVariables` drops
// undefined-valued keys but keeps `''` (as `"coupons":""`), which would fragment the cache
// key between a call with durable codes resolved-empty and a caller that omits the key
// entirely, reintroducing the redundant-fetch problem this merge is meant to fix.
export const csvOrUndefined = (codes: string[]) => codes.join(',') || undefined;

/**
 * Wire format of the `loyaltyCoupons` argument: the codes as CSV, prefixed with the customer's
 * choice in the PAYBACK conflict dialog (`preferDiscounts:CODE1,CODE2`) once one was made. The
 * choice is sent even without stored codes (`preferDiscounts:`), since the conflicting coupons
 * are often eCoupons the backend holds itself - same as the legacy checkout.
 */
export const serializeLoyaltyCoupons = (
  conflictBehavior: string | undefined,
  codes: string[],
): string | undefined =>
  conflictBehavior ? `${conflictBehavior}:${codes.join(',')}` : csvOrUndefined(codes);

/** `loyaltyCoupons` as currently held by the durable code store. */
export const storedLoyaltyCoupons = () =>
  serializeLoyaltyCoupons(
    apiKeyManager.getLoyaltyConflictBehavior(),
    apiKeyManager.getLoyaltyCodes(),
  );

export const usePreparedBasket = (
  variables: GetPreparedBasketQueryVariables,
  pause?: boolean,
  requestPolicy: RequestPolicy = 'cache-first',
) => {
  // Bakes the durable coupon/jö-code store into every caller's variables, so a customer who
  // already redeemed a code (e.g. via an affiliate link, before this page ever renders) gets
  // it reflected on the very first request instead of a second, separately-keyed one - `apiKeyManager`'s
  // getters are synchronous, so this costs nothing extra per render. Explicit caller values
  // still win via `??`, though no current caller passes them.
  const mergedVariables: GetPreparedBasketQueryVariables = {
    ...variables,
    coupons: variables.coupons ?? csvOrUndefined(apiKeyManager.getBonuscodes()),
    loyaltyCoupons: variables.loyaltyCoupons ?? storedLoyaltyCoupons(),
  };

  const [result, reexecute] = useAuthQuery({
    query: GetPreparedBasketDocument,
    variables: mergedVariables,
    requestPolicy,
    pause,
  });

  // A changed code store changes the variables, and a query for new variables starts without
  // data. The last answer for the same page stands in until the new one arrives, so a code change
  // shows the previous basket instead of the loading shell.
  const pageKey = `${mergedVariables.locale}|${mergedVariables.page}`;
  const [previous, setPrevious] = useState<{ pageKey: string; data: typeof result.data } | null>(
    null,
  );
  if (result.data && (previous?.data !== result.data || previous.pageKey !== pageKey)) {
    setPrevious({ pageKey, data: result.data });
  }
  const isAwaitingNewCodes =
    !result.data && !result.error && !pause && previous?.pageKey === pageKey;
  const current = isAwaitingNewCodes ? previous.data : result.data;

  const raw = current?.getPreparedBasket;
  const orderableBasket =
    raw?.__typename === 'CheckoutOrderableBasket'
      ? {
          __typename: 'CheckoutOrderableBasket' as const,
          ...getFragmentData(CheckoutOrderableBasketFragmentFragmentDoc, raw),
        }
      : raw;
  const data = current ? { getPreparedBasket: orderableBasket } : current;

  return [{ ...result, data, stale: result.stale || isAwaitingNewCodes }, reexecute] as const;
};
